import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent, FocusEvent, useState } from "react";

import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { hasValidationMessage } from "../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import sanitizeInput, { Sanitizers } from "./sanitizeInput";
import unobtrusiveValidation from "./unobtrusiveValidation";
import useFieldData from "./useFieldData";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

interface Props {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName?: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  inputValue?: number;
  id?: string;
  sanitize?: Sanitizers<number | undefined>;
  validators: Validators<number>;
  onChange: (value?: number) => void;
  propertyName: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  defaultInputValue?: number;
}

const DecimalInput = (props: Props) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, placeholder, suffix, prefix, tooltip } = useFieldData(props.fieldData, name);
  const [isDirty, setDirty] = useState(Boolean(props.inputValue));
  const sanitizedInput = sanitizeInput(props.inputValue, props.sanitize) ?? "";
  const modelState = props.modelState;

  if (props.defaultInputValue && !props.inputValue) {
    props.onChange(props.defaultInputValue);
  }

  const handleNumberInput = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const re = /^\d+(\.\d{1,2})?$/;
    if (re.test(inputValue)) {
      props.onChange(parseFloat(inputValue));
    }
    if (inputValue === "") {
      props.onChange(undefined);
    }
  };

  const handleNumberBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const re = /^\d+(\.\d{1,2})?$/;
    let dirty = isDirty;
    if (!dirty && inputValue) {
      setDirty(true);
      dirty = true;
    }

    if (props.onChange && (props.inputValue === undefined || re.test(inputValue))) {
      let number = parseFloat(inputValue);
      if (props.max && number > props.max) {
        number = props.max;
      }

      if (props.min && number < props.min) {
        number = props.min;
      }

      if (dirty && props.setModelState) {
        props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, number));
      }

      props.onChange(number);
    }
  };

  const id = props.id || fieldName;
  const hasError = hasValidationMessage(modelState, props.propertyName);
  const errorId = `${id}-error`;

  return (
    <div className="form-row">
      <div className="form-item range medium">
        <div className="form-input range">
          <div className="form-label">
            <label htmlFor={id}>{heading.value}</label>
            <Tooltip fieldName={fieldName} value={tooltip.value} />
          </div>
          {prefix.value && <div className="prefix">{prefix.value}</div>}
          <input
            id={id}
            required={props.required}
            name={fieldName}
            autoComplete="off"
            placeholder={placeholder.value}
            min={props.min}
            max={props.max}
            step={props.step || 1}
            type="number"
            lang="nl-NL"
            value={sanitizedInput}
            onChange={handleNumberInput}
            onBlur={handleNumberBlur}
            aria-invalid={hasError}
            aria-errormessage={hasError ? errorId : undefined}
          />
          {suffix.value && <div className="suffix">{suffix.value}</div>}
        </div>
      </div>
      <ValidationMessage modelState={modelState} property={props.propertyName} id={errorId} />
    </div>
  );
};

export default DecimalInput;
